import * as React from 'react';

import { ApolloQueryResult, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Image,
  Link,
  Skeleton,
  Stack,
  VStack,
  useColorModeValue,
  Text,
  Divider,
  Badge,
} from '@chakra-ui/react';
import sanitizeHtml from 'sanitize-html';
import { useParams } from 'react-router-dom';
import { Job, Query } from '~/generated/graphql';

import { LikeButton } from '~/components/molecules';
import { showJob } from '~/queries/job';
import { ImUserTie, ImBriefcase, ImOffice, ImDatabase } from 'react-icons/im';

const JobDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { loading, data } = useQuery(showJob, {
    variables: { id },
  }) as ApolloQueryResult<Pick<Query, 'job'>>;
  const backgroundColor = useColorModeValue('gray.800', 'white');

  if (loading)
    return (
      <Container>
        <Stack>
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
        </Stack>
      </Container>
    );

  const job = data.job as Job;
  const {
    title,
    company,
    coverImage,
    description,
    images,
    idealCandidate,
    characteristicLabels,
  } = job;

  return (
    <Box sx={{ maxW: '1280px', m: 'auto' }}>
      <Stack px={{ base: 0, md: '20%' }}>
        {coverImage && (
          <Image
            src={coverImage}
            sx={{
              fit: 'contain',
              objectPosition: 'center',
              w: '100%',
              maxW: 1280,
              maxH: 720,
            }}
          />
        )}
      </Stack>
      <Flex px={{ base: '10px', md: '10%', lg: '20%' }} py={{ base: 5, md: 7 }}>
        <VStack spacing={{ base: 5, md: 7 }} sx={{ alignItems: 'flex-start' }}>
          <Flex>
            <Image
              src={company.logo || undefined}
              alt={`${company.name}のロゴ`}
              sx={{
                fit: 'cover',
                objectFit: 'contain',
                objectPosition: 'center',
              }}
              maxWidth={{ base: '128px', md: '200px' }}
              mr={{ base: 3, md: 8 }}
            />
            <Stack spacing={{ base: 1, md: 4 }}>
              <Heading
                fontSize={{ base: 'md', md: '2xl' }}
                sx={{
                  fontWeight: 'bold',
                  color: backgroundColor,
                  wordBreak: 'break-all',
                }}
              >
                {title}
              </Heading>
              <Text sx={{ color: '#555', fontWeight: 'bold', fontSize: 'sm' }}>
                {company.name}
              </Text>
            </Stack>
          </Flex>
          <Divider />
          <Box>
            <Badge
              colorScheme="green"
              sx={{ p: 2 }}
              fontSize={{ base: 'xs', md: 'sm' }}
            >
              {company.industry}
            </Badge>
            <HStack mt={2} sx={{ flexWrap: 'wrap' }} spacing={0}>
              {characteristicLabels.map((characteristicLabel) => (
                <Badge
                  key={characteristicLabel.id}
                  style={{ margin: '0 10px 10px 0' }}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  sx={{ p: 2 }}
                >
                  {characteristicLabel.name}
                </Badge>
              ))}
            </HStack>
          </Box>

          <Divider />
          <Heading
            fontSize={{ base: 'xl', md: '2xl' }}
            sx={{ fontWeight: 'bold', color: backgroundColor }}
          >
            <Flex alignItems="center">
              <Badge
                colorScheme="green"
                sx={{
                  height: '40px',
                  width: '40px',
                  mr: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '100%',
                  borderRadius: '50%',
                }}
              >
                <ImBriefcase />
              </Badge>
              仕事内容
            </Flex>
          </Heading>
          <Box
            sx={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
          />
          <Divider />
          {images && images.length > 0 && (
            <>
              <HStack spacing={0}>
                {images.map((image) => (
                  <Flex key={image}>
                    <Image
                      src={image}
                      sx={{
                        maxHeight: '300px',
                        width: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Flex>
                ))}
              </HStack>
              <Divider />
            </>
          )}
          <Heading
            fontSize={{ base: 'xl', md: '2xl' }}
            sx={{ fontWeight: 'bold', color: backgroundColor }}
          >
            <Flex alignItems="center">
              <Badge
                colorScheme="green"
                sx={{
                  height: '40px',
                  width: '40px',
                  mr: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '100%',
                  borderRadius: '50%',
                }}
              >
                <ImOffice />
              </Badge>
              事業内容
            </Flex>
          </Heading>
          <Box
            sx={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(company.businessDescription || ''),
            }}
          />
          <Divider />
          <Heading
            fontSize={{ base: 'xl', md: '2xl' }}
            sx={{ fontWeight: 'bold', color: backgroundColor }}
          >
            <Flex alignItems="center">
              <Badge
                colorScheme="green"
                sx={{
                  height: '40px',
                  width: '40px',
                  mr: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '100%',
                  borderRadius: '50%',
                }}
              >
                <ImUserTie />
              </Badge>
              求める人物
            </Flex>
          </Heading>
          <Box
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(idealCandidate),
            }}
          />
          <Divider />
          <Heading
            fontSize={{ base: 'xl', md: '2xl' }}
            sx={{ fontWeight: 'bold', color: backgroundColor }}
          >
            <Flex alignItems="center">
              <Badge
                colorScheme="green"
                sx={{
                  height: '40px',
                  width: '40px',
                  mr: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '100%',
                  borderRadius: '50%',
                }}
              >
                <ImDatabase />
              </Badge>
              企業情報
            </Flex>
          </Heading>
          <VStack
            sx={{ alignItems: 'flex-start' }}
            spacing={8}
            py={{ base: 5, md: 3 }}
          >
            <VStack alignItems="flex-start">
              <Heading size="xs">会社名</Heading>
              <Text sx={{ fontSize: 'md' }}>{company.name}</Text>
            </VStack>
            <VStack sx={{ alignItems: 'flex-start' }}>
              <Heading size="xs">業種</Heading>
              <Text sx={{ fontSize: 'md' }}>{company.industry}</Text>
            </VStack>
            <VStack sx={{ alignItems: 'flex-start' }}>
              <Heading size="xs">本社所在地</Heading>
              <Text sx={{ fontSize: 'md' }}>{company.headquartersAddress}</Text>
            </VStack>
            <VStack sx={{ alignItems: 'flex-start' }}>
              <Heading size="xs">企業HP</Heading>
              <Text sx={{ fontSize: 'md' }}>
                {company.homepage && (
                  <Box>
                    <Link href={company.homepage} target="_blank">
                      {company.homepage}
                    </Link>
                  </Box>
                )}
              </Text>
            </VStack>
          </VStack>
        </VStack>
      </Flex>
      <Flex
        position="fixed"
        sx={{
          bottom: 0,
          display: 'flex',
          boxShadow: '5px 10px 20px rgba(0,0,0,0.25)',
          justifyContent: 'space-around',
          alignItems: 'center',
          backgroundColor: 'wh',
          background: 'rgba(255,255,255,0.9)',
        }}
        width={{ base: '100%', md: '300px' }}
        right={{ base: 0, md: 0 }}
        flexDirection={{ base: 'row', md: 'column' }}
        borderTop={{ base: '1px solid #ddd', md: 'none' }}
        height={{ base: '80px', md: '150px' }}
        py={{ base: 0, md: 4 }}
      >
        <LikeButton job={job} width="150px" colorScheme="green" />
        <Link
          href={`/company/${company.id}`}
          _hover={{ textDecoration: 'none' }}
        >
          <Button colorScheme="blue" width={{ base: '150px', md: '200px' }}>
            企業情報
          </Button>
        </Link>
      </Flex>
    </Box>
  );
};

export default JobDetails;
